// src/components\common\OnboardingFlow.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
} from '@mui/material';
import {
    Close as CloseIcon,
    ArrowForward as NextIcon,
    ArrowBack as BackIcon,
    CheckCircle as CheckIcon,
    Analytics as AnalyticsIcon,
    Science as ScienceIcon,
    MenuBook as DocsIcon,
    CalendarMonth as DemoIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

// Check if user has seen onboarding
const ONBOARDING_KEY = 'autoposture_onboarding_completed';
const hasSeenOnboarding = () => localStorage.getItem(ONBOARDING_KEY) === 'true';
const markOnboardingComplete = () => localStorage.setItem(ONBOARDING_KEY, 'true');

// Onboarding steps configuration
const onboardingSteps = [
    {
        title: 'Welcome to AutoPostureCV',
        subtitle: 'Automated posture analysis with computer vision',
        content: (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                    }}
                >
                    <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '2rem' }}>AP</Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    AutoPostureCV analyzes posture using 5 ergonomic frameworks to help researchers, 
                    students, workers, and businesses assess ergonomic risks.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="5 Frameworks" size="small" color="primary" />
                    <Chip label="Research Grade" size="small" color="secondary" />
                    <Chip label="Easy to Use" size="small" variant="outlined" />
                </Box>
            </Box>
        )
    },
    {
        title: 'Choose Your Experience',
        subtitle: 'Tell us who you are for a personalized experience',
        content: (
            <Grid container spacing={2} sx={{ py: 2 }}>
                {[
                    { key: 'researcher', label: 'Researcher', emoji: '🔬', desc: 'Advanced analysis & exports' },
                    { key: 'student', label: 'Student', emoji: '📚', desc: 'Learning & tutorials' },
                    { key: 'worker', label: 'Worker', emoji: '👷', desc: 'Simple risk assessment' },
                    { key: 'investor', label: 'Investor', emoji: '💼', desc: 'Business opportunities' },
                ].map((audience) => (
                    <Grid item xs={6} key={audience.key}>
                        <Card
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                                textAlign: 'center',
                                p: 2,
                            }}
                        >
                            <Typography variant="h4" sx={{ mb: 1 }}>{audience.emoji}</Typography>
                            <Typography variant="subtitle1" fontWeight={600}>{audience.label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {audience.desc}
                            </Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        )
    },
    {
        title: 'Key Features',
        subtitle: 'Everything you need for posture analysis',
        content: (
            <Grid container spacing={3} sx={{ py: 2 }}>
                {[
                    {
                        icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
                        title: 'Multi-Framework Analysis',
                        desc: 'HIPE, RULA, REBA, RAMP, and Wergonomics in one platform'
                    },
                    {
                        icon: <ScienceIcon sx={{ fontSize: 32, color: 'secondary.main' }} />,
                        title: 'Research Exports',
                        desc: 'CSV exports compatible with R, Python, SPSS, and more'
                    },
                    {
                        icon: <DocsIcon sx={{ fontSize: 32, color: 'warning.main' }} />,
                        title: 'Interactive Docs',
                        desc: 'Comprehensive guides and framework explanations'
                    },
                    {
                        icon: <DemoIcon sx={{ fontSize: 32, color: 'success.main' }} />,
                        title: 'Expert Support',
                        desc: 'Book demos and technical consultations'
                    },
                ].map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(0,0,0,0.04)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {feature.icon}
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    {feature.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {feature.desc}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        )
    },
    {
        title: 'Ready to Start?',
        subtitle: 'Choose your next step',
        content: (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    You're all set! Here are some recommended next steps based on your needs:
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
                                border: '2px solid',
                                borderColor: 'primary.main',
                            }}
                        >
                            <AnalyticsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>Start Analysis</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Upload a video and begin your first posture analysis
                            </Typography>
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <Paper 
                            sx={{ 
                                p: 3,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
                                border: '2px solid',
                                borderColor: 'secondary.main',
                            }}
                        >
                            <DocsIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>Explore Docs</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Learn about frameworks and analysis methods
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(37,99,235,0.05)', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        💡 <strong>Tip:</strong> You can change your user type anytime using the dropdown in the header.
                    </Typography>
                </Box>
            </Box>
        )
    }
];

// Main onboarding component
export const OnboardingFlow = ({ open, onClose, autoShow = false }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedAudience, setSelectedAudience] = useState(null);
    const navigate = useNavigate();
    const { dispatch } = useAppContext();

    // Auto-show onboarding for new users
    useEffect(() => {
        if (autoShow && !hasSeenOnboarding() && !open) {
            // Small delay to let the app load
            setTimeout(() => {
                onClose(false); // This should actually be onOpen, but keeping consistent with prop name
            }, 1000);
        }
    }, [autoShow, open, onClose]);

    const handleNext = () => {
        if (currentStep < onboardingSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleClose = (completed = false) => {
        if (completed || currentStep === onboardingSteps.length - 1) {
            markOnboardingComplete();
            
            // Set selected audience if chosen
            if (selectedAudience) {
                dispatch({ type: 'SET_AUDIENCE', payload: selectedAudience });
            }
        }
        
        setCurrentStep(0);
        setSelectedAudience(null);
        onClose();
    };

    const handleAudienceSelect = (audience) => {
        setSelectedAudience(audience);
        dispatch({ type: 'SET_AUDIENCE', payload: audience });
        handleNext();
    };

    const handleQuickAction = (action) => {
        handleClose(true);
        navigate(action);
    };

    const isLastStep = currentStep === onboardingSteps.length - 1;
    const currentStepData = onboardingSteps[currentStep];

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header with close button */}
                <Box sx={{ p: 3, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            {currentStepData.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {currentStepData.subtitle}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => handleClose(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Progress Stepper */}
                <Box sx={{ px: 3, py: 2 }}>
                    <Stepper activeStep={currentStep} alternativeLabel>
                        {onboardingSteps.map((step, index) => (
                            <Step key={index}>
                                <StepLabel>{step.title}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Content */}
                <Box sx={{ px: 3, minHeight: 300 }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {currentStep === 1 ? (
                                // Special handling for audience selection step
                                <Grid container spacing={2} sx={{ py: 2 }}>
                                    {[
                                        { key: 'researcher', label: 'Researcher', emoji: '🔬', desc: 'Advanced analysis & exports' },
                                        { key: 'student', label: 'Student', emoji: '📚', desc: 'Learning & tutorials' },
                                        { key: 'worker', label: 'Worker', emoji: '👷', desc: 'Simple risk assessment' },
                                        { key: 'investor', label: 'Investor', emoji: '💼', desc: 'Business opportunities' },
                                    ].map((audience) => (
                                        <Grid item xs={6} key={audience.key}>
                                            <Card
                                                onClick={() => handleAudienceSelect(audience.key)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    border: selectedAudience === audience.key ? '2px solid' : '1px solid',
                                                    borderColor: selectedAudience === audience.key ? 'primary.main' : 'divider',
                                                    '&:hover': { 
                                                        transform: 'translateY(-2px)', 
                                                        boxShadow: 4,
                                                        borderColor: 'primary.main',
                                                    },
                                                    textAlign: 'center',
                                                    p: 2,
                                                }}
                                            >
                                                <Typography variant="h4" sx={{ mb: 1 }}>{audience.emoji}</Typography>
                                                <Typography variant="subtitle1" fontWeight={600}>{audience.label}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {audience.desc}
                                                </Typography>
                                                {selectedAudience === audience.key && (
                                                    <CheckIcon sx={{ color: 'primary.main', mt: 1 }} />
                                                )}
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : currentStep === 3 ? (
                                // Special handling for final step with actions
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                                        You're all set! Here are some recommended next steps:
                                    </Typography>
                                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper 
                                                onClick={() => handleQuickAction('/analysis')}
                                                sx={{ 
                                                    p: 3, 
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
                                                    border: '2px solid',
                                                    borderColor: 'primary.main',
                                                }}
                                            >
                                                <AnalyticsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                                <Typography variant="h6" gutterBottom>Start Analysis</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Upload a video and begin your first posture analysis
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Paper 
                                                onClick={() => handleQuickAction('/documentation')}
                                                sx={{ 
                                                    p: 3,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
                                                    border: '2px solid',
                                                    borderColor: 'secondary.main',
                                                }}
                                            >
                                                <DocsIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                                                <Typography variant="h6" gutterBottom>Explore Docs</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Learn about frameworks and analysis methods
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(37,99,235,0.05)', borderRadius: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            💡 <strong>Tip:</strong> You can change your user type anytime using the dropdown in the header.
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                currentStepData.content
                            )}
                        </motion.div>
                    </AnimatePresence>
                </Box>

                {/* Footer with navigation buttons */}
                <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        startIcon={<BackIcon />}
                        color="inherit"
                    >
                        Back
                    </Button>

                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            {currentStep + 1} of {onboardingSteps.length}
                        </Typography>
                    </Box>

                    {isLastStep ? (
                        <Button
                            onClick={() => handleClose(true)}
                            variant="contained"
                            endIcon={<CheckIcon />}
                        >
                            Get Started
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            variant="contained"
                            endIcon={<NextIcon />}
                            disabled={currentStep === 1 && !selectedAudience}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// Hook to check if onboarding should be shown
export const useOnboarding = () => {
    const [shouldShow, setShouldShow] = useState(false);

    useEffect(() => {
        // Check if user is new and should see onboarding
        const isNewUser = !hasSeenOnboarding();
        setShouldShow(isNewUser);
    }, []);

    const markCompleted = () => {
        markOnboardingComplete();
        setShouldShow(false);
    };

    return {
        shouldShow,
        markCompleted,
        hasSeenOnboarding: hasSeenOnboarding(),
    };
};

export default OnboardingFlow;